import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useGetOngoingTrialsQuery } from '../../store/slices/apiSlice';
import { useSelector } from 'react-redux';
import _ from 'lodash';

// Roles that can use "Select All Sites" button
const ROLES_WITH_SELECT_ALL_SITES = ['site_monitor', 'data_manager'];

const WidgetSetting = ({ 
  trial, 
  selectedSites, 
  setTrial, 
  setSelectedSites, 
  siteList, 
  userRole,
  onSelectAllSites,
  widgetsPerRow,
  onWidgetsPerRowChange,
  ...props 
}) => {
  const { onClose, open, widgets, onWidgetSelection, onWidgetSelectAll } = props;
  const [allSelected, setAllSelected] = useState(false);
  const sponsorId = useSelector((state) => state?.userDetails?.loggedInUser.sponsorId);
  const { data: ongoingTrials } = useGetOngoingTrialsQuery(sponsorId);

  // Check if user can see the "Select All Sites" button
  const canSelectAllSites = useMemo(() => {
    return ROLES_WITH_SELECT_ALL_SITES.includes(userRole);
  }, [userRole]);

  // Check if all sites are already selected
  const allSitesSelected = useMemo(() => {
    if (!siteList || siteList.length === 0) return false;
    return siteList.every((site) => selectedSites?.includes(site.id));
  }, [siteList, selectedSites]);

  useEffect(() => {
    if (widgets) {
      const filteredVal = Object.values(widgets).filter((obj1) => obj1.display);
      setAllSelected(filteredVal.length == Object.values(widgets).length);
    }
  }, [widgets]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 600, // Set the width of the Drawer paper
            height: '100%',
            paddingTop: 8,
            backgroundColor: '#F5F5F5',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box>
          <Box sx={{ padding: 3 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#000' }}>Dashboard Setting</Typography>
          </Box>
          <Divider />
          <Box sx={{ padding: 3 }}>
            <Box sx={{ pb: 2 }}>
              <InputLabel>Study</InputLabel>
              <Select
                value={trial}
                onChange={(e) => {
                  setTrial(e.target.value);
                }}
                displayEmpty
                fullWidth
              >
                <MenuItem value="" disabled>
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select Study</em>
                </MenuItem>
                {ongoingTrials &&
                  ongoingTrials.map((trial, index) => (
                    <MenuItem key={index} value={trial.id}>
                      {trial.protocolNumber}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
            <Box sx={{ paddingY: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',  }}>
                <InputLabel sx={{ mb: 0 }}>Site</InputLabel>
                {canSelectAllSites && siteList?.length > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={onSelectAllSites}
                    disabled={allSitesSelected}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: allSitesSelected ? 'text.disabled' : 'primary.main',
                    }}
                  >
                    {allSitesSelected ? 'All Sites Selected' : 'Select All Sites'}
                  </Button>
                )}
              </Box>
              <Select
                value={selectedSites}
                onChange={(e) => {
                  if (selectedSites.length > 1 && selectedSites.includes(e.target.value)) {
                    const filtered = selectedSites.filter((siteId) => siteId !== e.target.value);
                    setSelectedSites(filtered);
                  } else if (!selectedSites.includes(e.target.value)) {
                    setSelectedSites([e.target.value, ...selectedSites]);
                  }
                }}
                fullWidth
                renderValue={() => {
                  const _vals = siteList.filter((site) => selectedSites?.includes(site.id));
                  if (_vals.length === siteList?.length && siteList?.length > 1) {
                    return 'All Sites';
                  }
                  return _vals.map((site) => site.siteName).join(', ');
                }}
              >
                <MenuItem value="" disabled>
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select Site</em>
                </MenuItem>
                {siteList &&
                  siteList?.map((site, index) => (
                    <MenuItem key={index} value={site.id}>
                      <Checkbox checked={selectedSites?.includes(site.id)} />
                      {site.siteName}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
            <Box sx={{ paddingY: 2 }}>
              <InputLabel sx={{ display: 'block', mb: 1 }}>Widgets per row</InputLabel>
              <RadioGroup
                row
                value={String(widgetsPerRow ?? 3)}
                onChange={(e) => onWidgetsPerRowChange?.(Number(e.target.value))}
                sx={{ opacity: selectedSites?.length > 3 ? 0.6 : 1 }}
              >
                <FormControlLabel value="1" control={<Radio />} label="1" disabled={selectedSites?.length > 3} />
                <FormControlLabel value="2" control={<Radio />} label="2" disabled={selectedSites?.length > 3} />
                <FormControlLabel value="3" control={<Radio />} label="3" disabled={selectedSites?.length > 3} />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {selectedSites?.length > 3
                  ? '1 widget per row when more than 3 sites are selected.'
                  : 'When more than 3 sites are selected, 1 widget per row is used regardless.'}
              </Typography>
            </Box>

            <Divider />
            <Box sx={{ paddingY: 2 }}>
              <InputLabel>Widget Preferences</InputLabel>
              <FormGroup>
                <FormControlLabel
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold' } }}
                  control={<Checkbox checked={allSelected} onChange={(e) => onWidgetSelectAll(e)} />}
                  label={'Select All'}
                />
                {Object.keys(widgets).map(
                  (key) =>
                    widgets[key].label && (
                      <FormControlLabel
                        key={`${key}wid`}
                        control={
                          <Checkbox checked={widgets[key].display} onChange={(e) => onWidgetSelection(e, key)} />
                        }
                        label={widgets[key].label}
                      />
                    )
                )}
              </FormGroup>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default WidgetSetting;
